/* Permissions (designs/organizer-roles.md R7), ported from renderPermissions
   (app.ts ~4942). One card per user who has signed in: the organizer roles they
   hold (granted ones revocable, derived ones read-only from the roster), a
   Grant control offering only grantable roles, a search filter, and the gated-
   actions key. Admin-only — the shell only mounts this view when the reader
   holds admin (sections()).

   Parity with the vanilla renderer is the spec (see CONTRACT.md). The on-nav
   read (store.openPermissions) already ran from selectNav, arming usersPending
   and clearing usersError; this view renders the loading / error / content
   states from state. Grant writes go through the bundle's own actions, each
   byte-identical to app.ts. */
import { useState, type JSX } from "react";
import { useAppState } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { Loading } from "../shell/Loading";
import { LoadError } from "../shell/LoadError";
import { iPanel } from "../shell/helpText";
import type { DerivedRole, RoleGrant } from "../../../shared/organizer-roles";
import { grantRole, revokeGrant } from "./rolesPermissions.actions";
import { ProblemContactsCard } from "./ProblemContacts";
import type { Store } from "../shell/store";
import type { ClientState, UserWithGrants } from "../shell/state";

const contents = { display: "contents" } as const;

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

/* only actions the server actually gates today — later use cases append */
const GATED_ACTIONS = [
  { action: "Grant and revoke organizer roles", carrier: "Admin (organizer role)" },
  {
    action: "Create and edit records (projects, allocations, portfolio, mappings)",
    carrier: "Record Keeper or Admin (organizer roles)",
  },
  { action: "Delete a project or portfolio item, history included", carrier: "Admin (organizer role)" },
  { action: "Change who to contact about problems", carrier: "Admin (organizer role)" },
];

const organizerRoleName = (state: ClientState, roleId: string): string =>
  state.organizerRoles.find((r) => r.id === roleId)?.name ?? roleId;

/* raw-id fallback like stale roleIds — the roster may no longer know it */
const teamName = (state: ClientState, teamId: string): string =>
  state.teams.find((t) => t.id === teamId)?.name ?? teamId;

/* identity-derived strings (names, roster team names) are escaped for free by
   JSX's {…} text nodes — the same protection the vanilla esc() gave (PR #11) */
function GrantRow({
  state,
  store,
  grant,
}: {
  state: ClientState;
  store: Store;
  grant: RoleGrant;
}): JSX.Element {
  const env = grant.recorded.source === "ADMIN_USERS environment";
  const note = env
    ? "held via the ADMIN_USERS environment"
    : `granted by ${grant.grantedBy.name} · ${fmtDate(grant.recorded.date)}`;
  return (
    <li>
      <span className="slot-main">
        <span className="slot-name">
          {organizerRoleName(state, grant.roleId)}
          {grant.teamId ? ` · ${teamName(state, grant.teamId)}` : ""}
          <span className="stamp">{grant.recorded.source}</span>
          {env ? null : (
            <button
              className="revoke-grant btn ghost small"
              onClick={() => void revokeGrant(store, grant.userId, grant.roleId, grant.teamId)}
            >
              Revoke
            </button>
          )}
        </span>
        <div className="slot-note">{note}</div>
      </span>
    </li>
  );
}

/* a derived role — from the roster, read-only, revocable only where the roster
   is managed (Entra) (R12) */
function DerivedRow({ state, derived }: { state: ClientState; derived: DerivedRole }): JSX.Element {
  return (
    <li>
      <span className="slot-main">
        <span className="slot-name">
          {organizerRoleName(state, derived.roleId)} · {teamName(state, derived.teamId)}
          <span className="stamp">{derived.source}</span>
        </span>
        <div className="slot-note">
          from the team roster — they manage this team; changes happen in Entra
        </div>
      </span>
    </li>
  );
}

function UserCard({
  state,
  store,
  user,
}: {
  state: ClientState;
  store: Store;
  user: UserWithGrants;
}): JSX.Element {
  /* only grantable roles are offered — derived ones live in the roster (R12) */
  const options = state.organizerRoles.filter((r) => r.assignment === "granted");
  /* the vanilla select was uncontrolled and grantRole read its .value at click
     time; React holds that choice here, defaulting to the first option exactly
     as an untouched <select> reports its first option's value */
  const [chosen, setChosen] = useState<string>(options[0]?.id ?? "");
  const rows: JSX.Element[] = [
    ...user.grants.map((g) => (
      <GrantRow key={`g:${g.roleId}:${g.teamId ?? ""}`} state={state} store={store} grant={g} />
    )),
    ...(user.derived ?? []).map((d) => (
      <DerivedRow key={`d:${d.roleId}:${d.teamId}`} state={state} derived={d} />
    )),
  ];
  return (
    <section className="card confirmed" data-user-card>
      <div className="card-head">
        <div className="title">{user.name}</div>
        <span className="stamp">
          {user.firstSeen ? `${user.firstSeen.source} · ${fmtDate(user.firstSeen.date)}` : "Not signed in yet"}
        </span>
      </div>
      <ul className="slots">
        {rows.length ? (
          rows
        ) : (
          <li>
            <span className="slot-main">
              <div className="slot-note">No organizer roles.</div>
            </span>
          </li>
        )}
      </ul>
      <div className="form-actions">
        <select
          className="grant-role"
          aria-label={`Role to grant ${user.name}`}
          value={chosen}
          onChange={(e) => setChosen(e.target.value)}
        >
          {options.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
        <button
          className="grant-role-btn btn ghost small"
          onClick={() => void grantRole(store, user.userId, chosen)}
        >
          Grant role
        </button>
      </div>
    </section>
  );
}

export function PermissionsView(): JSX.Element {
  const { state, store } = useAppState();
  if (state.usersError) return <LoadError what="permissions" />;
  /* "0 users have signed in" is a finding about the org, not a placeholder
     (review finding 2) — so pending is tested before that gap is drawn */
  if (state.usersPending) return <Loading what="permissions" />;

  const wanted = state.userSearch.trim().toLowerCase();
  const matching = wanted
    ? state.users.filter((u) => u.name.toLowerCase().includes(wanted))
    : state.users;

  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>Permissions.</b>
          <span>
            {state.users.length} users have signed in — organizer roles are app-wide or team-scoped;
            project roles live on each project.
          </span>
        </div>
      }
      footnote={
        "Organizer roles come from the canonical list — never free text. Every grant carries who granted it and when; revoking keeps the history."
      }
    >
      {state.listError ? (
        <p className="form-error" role="alert" tabIndex={-1}>
          {state.listError}
        </p>
      ) : null}
      {/* who to contact about problems (problem-contacts.md PC6) */}
      <ProblemContactsCard />
      <label className="field user-search-field">
        <span className="field-label">Find a user</span>
        <input
          id="user-search"
          type="search"
          value={state.userSearch}
          placeholder="Type a name…"
          onChange={(e) => store.update({ userSearch: e.target.value })}
        />
      </label>
      {matching.map((user) => (
        <UserCard key={user.userId} state={state} store={store} user={user} />
      ))}
      {/* the ⓘ concept panel — trusted help HTML, display:contents so it sits
          as a sibling exactly as the vanilla innerHTML did */}
      <div
        style={contents}
        dangerouslySetInnerHTML={{ __html: iPanel(state.me.surface, "who-can-do-what") }}
      />
      <div className="gated-actions" id="gated-actions">
        <div className="field-label">Gated actions</div>
        <ul>
          {GATED_ACTIONS.map((g) => (
            <li key={g.action}>
              {g.action} — <b>{g.carrier}</b>
            </li>
          ))}
        </ul>
      </div>
    </ViewFrame>
  );
}
